import React from "react";
import { flushSync } from "react-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { createApi } from "../../../../utils/createApi";
import { OrangeTextButton } from "../../../../common/OrangeTextButton";
import BackdropLoadingScreen from "../../../../common/BackdropLoadingScreen";
import { addIgnoreLabel } from "../SiteAboutPageUtils/addIgnoreLabel";
import { FlexCameraSummary, FlexPreset } from "./types";
import { FlexIgnoreZonesCameraPresetCard } from "./FlexIgnoreZonesCameraPresetCard";
import { deletePersistedFlexPresetRecordsAfterIgnoreZonesSave } from "./flexIgnoreZonesPresetCleanup";
import { useFlexIgnoreZonesOnboardingStore } from "./flexIgnoreZonesOnboardingStore";
import {
  FLEX_IZ_CREATE_NEW_PRESET_OPTION,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  FLEX_IZ_NO_IGNORE_ZONES_OPTION,
} from "./flexIzPresetSelectConstants";
import { FlexIgnoreZonesNewPresetDialog } from "./FlexIgnoreZonesNewPresetDialog";
import {
  collapseNoIgnoreZonesPresetOptions,
  filterFlexPresetsForApiPresetDropdown,
  isNoIgnorePreset,
  normalizeFlexPresetSelectionToApiBackedMenuValue,
} from "./flexPresetDisplayDedupe";
import {
  buildNoIgnoreZonesPostPayload,
  ignoreZoneStreamNameForCameraApi,
} from "./flexIgnoreZonesOffboardingPayload";

type CameraChoice = {
  mode: "preset" | "none";
  presetLocalId: string;
};

const replaceNegativeWithZero = (data: number[][][]): number[][][] => {
  const cloned = data.map((polygon) =>
    polygon.map((coordinates) => coordinates.map((num) => (num < 0 ? 0 : num)))
  );
  return cloned;
};

export const FlexIgnoreZonesOffboardingDialog = ({
  open,
  siteId,
  currentScheduleId,
  cameras,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  siteId: number;
  currentScheduleId?: number;
  cameras: FlexCameraSummary[];
  onClose: () => void;
  onSubmitted: () => void;
}) => {
  const {
    loadCameraContext,
    presetsByCamera,
    cameraContexts,
    selectedPresetByScheduleCamera,
    createCustomPresetDraft,
    removeLocalPresetDraft,
  } = useFlexIgnoreZonesOnboardingStore();

  const [choicesByCamera, setChoicesByCamera] = React.useState<
    Record<number, CameraChoice>
  >({});
  const [newPresetDialog, setNewPresetDialog] = React.useState<{
    cameraId: number;
    presetLocalId: string;
    cameraName: string;
  } | null>(null);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [previewRetryTriggeredByCamera, setPreviewRetryTriggeredByCamera] =
    React.useState<Record<number, boolean>>({});

  const resolvePreferredPreset = React.useCallback(
    (cameraId: number): string | null => {
      // "Default" (no preset assigned) is not a preset to keep after offboarding.
      const isPresetSelection = (presetLocalId: string | undefined) =>
        !!presetLocalId && presetLocalId !== FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION;
      if (typeof currentScheduleId === "number") {
        const fromCurrentSchedule =
          selectedPresetByScheduleCamera[`${currentScheduleId}:${cameraId}`];
        if (isPresetSelection(fromCurrentSchedule)) {
          return fromCurrentSchedule;
        }
      }

      const fromAnySchedule = Object.entries(selectedPresetByScheduleCamera).find(
        ([key, presetLocalId]) =>
          key.endsWith(`:${cameraId}`) && isPresetSelection(presetLocalId)
      );
      if (fromAnySchedule?.[1]) {
        return fromAnySchedule[1];
      }

      const presets = collapseNoIgnoreZonesPresetOptions(
        filterFlexPresetsForApiPresetDropdown(presetsByCamera[cameraId] || []),
        undefined
      );
      if (presets.length > 0) {
        return presets[0].localId;
      }

      return null;
    },
    [currentScheduleId, selectedPresetByScheduleCamera, presetsByCamera]
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setSubmitError(null);
    setSubmitLoading(false);
    setPreviewRetryTriggeredByCamera({});

    cameras.forEach((camera) => {
      loadCameraContext(camera.id);
    });
  }, [open, cameras, loadCameraContext]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setChoicesByCamera((previous) => {
      const next = { ...previous };
      cameras.forEach((camera) => {
        const existingChoice = next[camera.id];
        if (existingChoice) {
          if (existingChoice.mode === "preset") {
            const all = presetsByCamera[camera.id] || [];
            const apiOnly = filterFlexPresetsForApiPresetDropdown(all);
            const normalized = normalizeFlexPresetSelectionToApiBackedMenuValue(
              camera.id,
              all,
              existingChoice.presetLocalId
            );
            const stillExists =
              apiOnly.some((preset) => preset.localId === normalized) ||
              apiOnly.some((preset) => preset.localId === existingChoice.presetLocalId);
            if (!stillExists) {
              const fallback = resolvePreferredPreset(camera.id);
              next[camera.id] = fallback
                ? { mode: "preset", presetLocalId: fallback }
                : { mode: "none", presetLocalId: "" };
            }
          } else if (existingChoice.mode === "none") {
            const apiOnly = filterFlexPresetsForApiPresetDropdown(
              presetsByCamera[camera.id] || []
            );
            const collapsed = collapseNoIgnoreZonesPresetOptions(apiOnly, undefined);
            const persistedNoIgnore = collapsed.find(isNoIgnorePreset);
            if (persistedNoIgnore) {
              next[camera.id] = {
                mode: "preset",
                presetLocalId: persistedNoIgnore.localId,
              };
            }
          }
          return;
        }
        const preferred = resolvePreferredPreset(camera.id);
        next[camera.id] = preferred
          ? { mode: "preset", presetLocalId: preferred }
          : { mode: "none", presetLocalId: "" };
      });
      return next;
    });
  }, [open, cameras, presetsByCamera, resolvePreferredPreset]);

  const selectionComplete = cameras.every((camera) => {
    const choice = choicesByCamera[camera.id];
    if (!choice) {
      return false;
    }
    if (choice.mode === "none") {
      return true;
    }
    return !!choice.presetLocalId;
  });

  const getPresetForCameraChoice = (camera: FlexCameraSummary): FlexPreset => {
    const choice = choicesByCamera[camera.id];
    const selectedPreset =
      choice?.mode === "preset"
        ? (presetsByCamera[camera.id] || []).find(
            (preset) => preset.localId === choice.presetLocalId
          )
        : undefined;
    return (
      selectedPreset || {
        localId: `none-${camera.id}`,
        cameraId: camera.id,
        name: "No Ignore Zones",
        zones: [],
        persistedId: null,
        source: "custom",
        userNamed: true,
      }
    );
  };

  const handleSelectValue = (camera: FlexCameraSummary, value: string) => {
    if (value === FLEX_IZ_CREATE_NEW_PRESET_OPTION) {
      const contextDefaultZones = cameraContexts[camera.id]?.defaultZones;
      const fallbackZones =
        contextDefaultZones && contextDefaultZones.length > 0
          ? contextDefaultZones
          : addIgnoreLabel(camera.ignore_zones || []);
      const localId = createCustomPresetDraft(camera.id, fallbackZones);
      setNewPresetDialog({
        cameraId: camera.id,
        presetLocalId: localId,
        cameraName: camera.camera_name,
      });
      return;
    }

    if (value === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
      setChoicesByCamera((previous) => ({
        ...previous,
        [camera.id]: { mode: "none", presetLocalId: "" },
      }));
      return;
    }

    setChoicesByCamera((previous) => ({
      ...previous,
      [camera.id]: { mode: "preset", presetLocalId: value },
    }));
  };

  const handleSubmit = async () => {
    if (!selectionComplete) {
      alert("Please make a choice for every camera before submitting.");
      return;
    }
    flushSync(() => {
      setSubmitLoading(true);
      setSubmitError(null);
    });
    const AxiosUI = createApi("");

    try {
      for (const camera of cameras) {
        const choice = choicesByCamera[camera.id];
        if (choice.mode === "none") {
          const payload = await buildNoIgnoreZonesPostPayload(
            camera,
            cameraContexts[camera.id]?.defaultZones,
            AxiosUI
          );
          await AxiosUI.post(`/camera/${camera.id}/ignore_zones/`, payload);
          continue;
        }
        const selectedPreset =
          (presetsByCamera[camera.id] || []).find(
            (preset) => preset.localId === choice.presetLocalId
          ) || null;
        const payload = (selectedPreset?.zones || []).map((zone) => ({
          zone_type: zone.zone_type,
          stream_id: zone.stream_id,
          stream_name: ignoreZoneStreamNameForCameraApi(zone),
          metric_id: zone.metric_id,
          label: zone.label,
          label_display_name: zone.label_display_name,
          line_color: zone.line_color,
          coordinates: replaceNegativeWithZero(
            ((zone.coordinates || []).filter(
              (arr) => arr.length > 0
            ) as unknown as number[][][])
          ),
        }));

        await AxiosUI.post(`/camera/${camera.id}/ignore_zones/`, payload);
      }

      await deletePersistedFlexPresetRecordsAfterIgnoreZonesSave(
        AxiosUI,
        presetsByCamera,
        cameras,
        loadCameraContext
      );

      setSubmitLoading(false);
      onSubmitted();
      onClose();
    } catch (error: any) {
      setSubmitLoading(false);
      setSubmitError(error?.message || "Failed to apply ignore zones selections.");
    }
  };

  return (
    <>
      <BackdropLoadingScreen
        openState={submitLoading}
        message="Applying camera ignore zones selections..."
        stackAboveModal
      />
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (!submitLoading && reason !== "backdropClick") {
            onClose();
          }
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            fontFamily: "Mulish",
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
          Flex Ignore Zones Offboarding
        </DialogTitle>
        <DialogContent dividers>
          <div>
            <p className="font-mulish text-[14px] pb-[12px]">
              Choose what each camera should keep after turning Flex Ignore Zones
              off. The preview shows the currently selected zones for each camera.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start max-w-[1500px]">
              {cameras.map((camera) => {
                const choice = choicesByCamera[camera.id];
                const apiOnly = filterFlexPresetsForApiPresetDropdown(
                  presetsByCamera[camera.id] || []
                );
                const menuPresets = collapseNoIgnoreZonesPresetOptions(
                  apiOnly,
                  choice?.mode === "preset" ? choice.presetLocalId : undefined
                );
                const showSyntheticNoIgnoreOption = !menuPresets.some(isNoIgnorePreset);
                const selectedValue =
                  choice?.mode === "none"
                    ? FLEX_IZ_NO_IGNORE_ZONES_OPTION
                    : choice?.presetLocalId || "";

                const presetSelector = (
                  <FormControl fullWidth size="small">
                    <InputLabel
                      id={`offboarding-preset-select-${siteId}-${camera.id}`}
                      sx={{ fontFamily: "Mulish" }}
                    >
                      Ignore Zones Selection
                    </InputLabel>
                    <Select
                      labelId={`offboarding-preset-select-${siteId}-${camera.id}`}
                      label="Ignore Zones Selection"
                      value={selectedValue}
                      onChange={(event) =>
                        handleSelectValue(camera, String(event.target.value))
                      }
                      sx={{ fontFamily: "Mulish" }}
                    >
                      {menuPresets.length === 0 && (
                        <MenuItem disabled value="" sx={{ fontFamily: "Mulish" }}>
                          No presets available
                        </MenuItem>
                      )}
                      {menuPresets.map((preset) => (
                        <MenuItem
                          key={preset.localId}
                          value={preset.localId}
                          sx={{ fontFamily: "Mulish" }}
                        >
                          {preset.name}
                        </MenuItem>
                      ))}
                      <MenuItem
                        value={FLEX_IZ_CREATE_NEW_PRESET_OPTION}
                        sx={{
                          color: "#FF8400",
                          fontFamily: "Mulish",
                          fontWeight: 700,
                        }}
                      >
                        + CREATE NEW IGNORE ZONES
                      </MenuItem>
                      {showSyntheticNoIgnoreOption && (
                        <MenuItem
                          value={FLEX_IZ_NO_IGNORE_ZONES_OPTION}
                          sx={{ fontFamily: "Mulish" }}
                        >
                          No Ignore Zones
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                );

                return (
                  <FlexIgnoreZonesCameraPresetCard
                    key={`offboarding-camera-${camera.id}`}
                    scheduleId={0}
                    camera={camera}
                    preset={getPresetForCameraChoice(camera)}
                    cameraContext={cameraContexts[camera.id]}
                    presetLineReplacement={presetSelector}
                    onPreviewImageError={() => {
                      if (previewRetryTriggeredByCamera[camera.id]) {
                        return;
                      }
                      setPreviewRetryTriggeredByCamera((previous) => ({
                        ...previous,
                        [camera.id]: true,
                      }));
                      loadCameraContext(camera.id, true);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {submitError && (
            <p className="font-mulish text-[14px] text-red-600 pt-[12px]">
              {submitError}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <OrangeTextButton
            onClick={onClose}
            text="Cancel"
            disabled={submitLoading}
          />
          <OrangeTextButton
            onClick={handleSubmit}
            text="Submit"
            disabled={!selectionComplete || submitLoading}
          />
        </DialogActions>
      </Dialog>

      <FlexIgnoreZonesNewPresetDialog
        open={newPresetDialog != null}
        cameraId={newPresetDialog?.cameraId ?? 0}
        presetLocalId={newPresetDialog?.presetLocalId ?? null}
        cameraName={newPresetDialog?.cameraName ?? ""}
        assignToSchedule={false}
        onClose={(reason, meta) => {
          if (reason === "submitted" && newPresetDialog) {
            const nextLocalId = meta?.presetLocalId ?? newPresetDialog.presetLocalId;
            setChoicesByCamera((previous) => ({
              ...previous,
              [newPresetDialog.cameraId]: {
                mode: "preset",
                presetLocalId: nextLocalId,
              },
            }));
          }
          if (reason === "cancelled" && newPresetDialog) {
            removeLocalPresetDraft(
              newPresetDialog.cameraId,
              newPresetDialog.presetLocalId
            );
          }
          setNewPresetDialog(null);
        }}
      />
    </>
  );
};
